import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import Layout from '../layout/Layout';
import Reveal from './Reveal';
import SectionHead from './SectionHead';
import '../../styles/enterprise.css';

/* -------------------------------------------------------------------------- */
/* Small internal pieces                                                      */
/* -------------------------------------------------------------------------- */

const Action = ({ action, variant, index = 0 }) => {
  if (!action) return null;
  const { label, to, href } = action;
  const className = `ax-btn ax-btn--${variant}`;
  const content = (
    <>
      <span>{label}</span>
      {index === 0 && <ArrowRight className="ax-btn__arrow" size={17} />}
    </>
  );

  if (href) {
    return (
      <a className={className} href={href}>
        {content}
      </a>
    );
  }
  return (
    <Link className={className} to={to || '/contact'}>
      {content}
    </Link>
  );
};

const Hero = ({ eyebrow, title, lede, image, imageAlt, actions, breadcrumb }) => (
  <section className="ax-hero">
    {image && (
      <div className="ax-hero__bg">
        <img src={image} alt={imageAlt || ''} />
      </div>
    )}
    <div className="ax-hero__scrim" />
    <div className="ax-hero__inner">
      <div className="ax-container">
        <div className="ax-hero__copy">
          {breadcrumb !== false && (
            <Reveal className="ax-crumbs">
              <Link to="/">Home</Link>
              <ChevronRight />
              <Link to="/service">Services</Link>
            </Reveal>
          )}
          {eyebrow && (
            <Reveal delay={60} style={{ display: 'block', marginTop: 28 }}>
              <span className="ax-eyebrow">{eyebrow}</span>
            </Reveal>
          )}
          <Reveal delay={120}>
            <h1 className="ax-display">{title}</h1>
          </Reveal>
          {lede && (
            <Reveal delay={180}>
              <p className="ax-hero__lede">{lede}</p>
            </Reveal>
          )}
          {actions && actions.length > 0 && (
            <Reveal delay={240} className="ax-hero__actions">
              {actions.map((action, i) => (
                <Action
                  key={action.label}
                  action={action}
                  index={i}
                  variant={i === 0 ? 'invert' : 'ghost-invert'}
                />
              ))}
            </Reveal>
          )}
        </div>
      </div>
    </div>
  </section>
);

const MetricStrip = ({ items, ink }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="ax-metrics" style={{ '--ax-metrics-count': Math.min(items.length, 4) }}>
      {items.map((item, i) => (
        <Reveal key={item.label} className="ax-metric" delay={i * 70}>
          <span className="ax-metric__value">{item.value}</span>
          <span className="ax-metric__label">{item.label}</span>
        </Reveal>
      ))}
    </div>
  );
};

const CardGrid = ({ items, columns = 4 }) => (
  <div className={`ax-grid ax-grid--${columns} ax-grid--hairline`}>
    {items.map((item, i) => {
      const Icon = item.icon;
      return (
        <Reveal key={item.title} className="ax-card" delay={(i % columns) * 70}>
          {Icon && (
            <span className="ax-card__icon">
              <Icon />
            </span>
          )}
          <h3 className="ax-h3">{item.title}</h3>
          <p className="ax-body">{item.description}</p>
        </Reveal>
      );
    })}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Enterprise service-detail page shell.
 *
 * Every section is optional — pass only the content a given service actually
 * has, and the page composes itself in a consistent order:
 *
 *   Hero -> Overview -> Capabilities -> Process -> Outcomes -> CTA
 */
const ServicePage = ({
  hero,
  overview,
  capabilities,
  coverage,
  process,
  outcomes,
  cta,
}) => {
  useEffect(() => {
    document.body.classList.add('ibm-plex-page');
    return () => document.body.classList.remove('ibm-plex-page');
  }, []);

  return (
    <Layout header={6} footer={6}>
      <main className="ax">
        <Hero {...hero} />

        {/* --- At-a-glance figures, carried straight from the hero ---------- */}
        {hero && hero.metrics && hero.metrics.length > 0 && (
          <div className="ax-container">
            <MetricStrip items={hero.metrics} />
          </div>
        )}

        {/* --- Overview: what it is, why it matters ------------------------- */}
        {overview && (
          <section className="ax-section">
            <div className="ax-container">
              <div className="ax-split">
                <div>
                  <SectionHead
                    eyebrow={overview.eyebrow || 'Overview'}
                    title={overview.title}
                    lede={overview.lede}
                  />
                  {overview.body && overview.body.length > 0 && (
                    <Reveal delay={80} style={{ marginTop: 28 }}>
                      {overview.body.map((paragraph) => (
                        <p key={paragraph} className="ax-body" style={{ marginBottom: 16 }}>
                          {paragraph}
                        </p>
                      ))}
                    </Reveal>
                  )}
                  {overview.checklist && overview.checklist.length > 0 && (
                    <Reveal delay={120}>
                      <ul className="ax-checklist" style={{ marginTop: 32 }}>
                        {overview.checklist.map((entry) => (
                          <li key={entry}>
                            <Check />
                            <span>{entry}</span>
                          </li>
                        ))}
                      </ul>
                    </Reveal>
                  )}
                </div>

                {overview.image && (
                  <Reveal delay={140}>
                    <figure className="ax-media ax-media--tall">
                      <img src={overview.image} alt={overview.imageAlt || ''} />
                      {overview.imageCaption && (
                        <figcaption className="ax-media__caption">
                          <p className="ax-h4" style={{ color: '#fff' }}>
                            {overview.imageCaption}
                          </p>
                        </figcaption>
                      )}
                    </figure>
                  </Reveal>
                )}
              </div>
            </div>
          </section>
        )}

        {/* --- Capabilities ------------------------------------------------- */}
        {capabilities && capabilities.items && capabilities.items.length > 0 && (
          <section className="ax-section ax-section--alt">
            <div className="ax-container">
              <SectionHead
                eyebrow={capabilities.eyebrow || 'Capabilities'}
                title={capabilities.title}
                lede={capabilities.lede}
              />
              <div style={{ marginTop: 56 }}>
                <CardGrid
                  items={capabilities.items}
                  columns={capabilities.items.length % 3 === 0 && capabilities.items.length !== 4 ? 3 : 4}
                />
              </div>
            </div>
          </section>
        )}

        {/* --- Coverage: the domains this service spans --------------------- */}
        {coverage && coverage.items && coverage.items.length > 0 && (
          <section className="ax-section">
            <div className="ax-container">
              <SectionHead
                eyebrow={coverage.eyebrow || 'Coverage'}
                title={coverage.title}
                lede={coverage.lede}
              />
              <div style={{ marginTop: 56 }}>
                <div
                  className={`ax-grid ax-grid--${coverage.items.length % 2 === 0 ? 2 : 3} ax-grid--hairline`}
                >
                  {coverage.items.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Reveal key={item.title} className="ax-card" delay={(i % 2) * 70}>
                        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                          {Icon && (
                            <span className="ax-card__icon" style={{ marginBottom: 0 }}>
                              <Icon />
                            </span>
                          )}
                          <div>
                            <h3 className="ax-h3" style={{ marginBottom: 10 }}>{item.title}</h3>
                            <p className="ax-body">{item.description}</p>
                            {item.meta && (
                              <p
                                className="ax-label ax-numeral"
                                style={{ marginTop: 16 }}
                              >
                                {item.meta}
                              </p>
                            )}
                          </div>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* --- Process ------------------------------------------------------ */}
        {process && process.steps && process.steps.length > 0 && (
          <section className="ax-section">
            <div className="ax-container">
              <SectionHead
                eyebrow={process.eyebrow || 'Methodology'}
                title={process.title}
                lede={process.lede}
              />
              <div
                className="ax-process"
                style={{ marginTop: 56, '--ax-process-count': process.steps.length }}
              >
                {process.steps.map((step, i) => (
                  <Reveal key={step.title} className="ax-process__step" delay={i * 80}>
                    <span className="ax-process__num">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="ax-h4">{step.title}</h3>
                    <p className="ax-body">{step.description}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* --- Outcomes ----------------------------------------------------- */}
        {outcomes && (
          <section className="ax-section ax-section--ink">
            <div className="ax-container">
              <SectionHead
                eyebrow={outcomes.eyebrow || 'Outcomes'}
                title={outcomes.title}
                lede={outcomes.lede}
              />
              {outcomes.metrics && outcomes.metrics.length > 0 && (
                <div style={{ marginTop: 56 }}>
                  <MetricStrip items={outcomes.metrics} ink />
                </div>
              )}
              {outcomes.items && outcomes.items.length > 0 && (
                <div
                  className={`ax-grid ax-grid--${outcomes.items.length % 2 === 0 ? 2 : 3} ax-grid--gap`}
                  style={{ marginTop: 56 }}
                >
                  {outcomes.items.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Reveal
                        key={item.title}
                        delay={(i % 2) * 70}
                        style={{
                          borderTop: '2px solid rgba(255,255,255,0.22)',
                          paddingTop: 24,
                        }}
                      >
                        {Icon && (
                          <Icon
                            size={20}
                            style={{ color: 'var(--ax-blue-300)', marginBottom: 16 }}
                          />
                        )}
                        <h3 className="ax-h4" style={{ color: '#fff', marginBottom: 10 }}>
                          {item.title}
                        </h3>
                        <p className="ax-body" style={{ color: 'rgba(255,255,255,0.74)' }}>
                          {item.description}
                        </p>
                      </Reveal>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* --- Closing CTA --------------------------------------------------- */}
        {cta !== null && (
          <section className="ax-section ax-section--tight ax-rule">
            <div className="ax-container">
              <div
                className="ax-split"
                style={{ alignItems: 'center', gap: 'clamp(24px, 4vw, 64px)' }}
              >
                <Reveal>
                  <span className="ax-eyebrow">{(cta && cta.eyebrow) || 'Next step'}</span>
                  <h2 className="ax-h2" style={{ marginTop: 16 }}>
                    {(cta && cta.title) || 'Talk to our engineering team'}
                  </h2>
                  {cta && cta.body && (
                    <p className="ax-lede" style={{ marginTop: 16 }}>
                      {cta.body}
                    </p>
                  )}
                </Reveal>
                <Reveal
                  delay={80}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'flex-start' }}
                >
                  <Action
                    action={(cta && cta.primary) || { label: 'Contact us', to: '/contact' }}
                    variant="primary"
                    index={0}
                  />
                  <Action
                    action={(cta && cta.secondary) || { label: 'View all services', to: '/service' }}
                    variant="secondary"
                    index={1}
                  />
                </Reveal>
              </div>
            </div>
          </section>
        )}
      </main>
    </Layout>
  );
};

export default ServicePage;
